/**
 * Reproductor del video del punto.
 *
 * Maneja dos tipos de fuente, que se comportan de forma distinta:
 *
 *  - Archivo propio: elemento video del navegador. Intenta arrancar con sonido
 *    y, si el navegador no lo deja (iOS siempre, sin un toque sobre el propio
 *    video), arranca en silencio con un boton para activar el sonido. La
 *    decision vive en `lib/reproducir.ts`.
 *  - YouTube: iframe. Solo puede arrancar automaticamente en silencio, asi que
 *    se avisa a la persona de que active el sonido.
 *
 * Si el navegador rechaza incluso el arranque en silencio (iOS en ahorro de
 * energia, o un fichero que no carga), no queda una pantalla negra: aparece un
 * boton grande de reproducir.
 *
 * Accesibilidad: controles nativos (los del navegador ya son accesibles y
 * estan traducidos por el sistema), pista de subtitulos cuando existe, y
 * descripcion textual del contenido para quien no puede ver el video.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  analizarFuenteVideo,
  ordenYoutube,
  urlIncrustadaYoutube,
  type PuntoPublico,
} from '@recorregut/shared';
import { idiomaActivo } from '@/i18n';
import { reproducirConRespaldo } from '@/lib/reproducir.ts';

interface Props {
  punto: PuntoPublico;
  /** Si debe intentar arrancar solo, que es el caso de la primera visita. */
  automatico: boolean;
}

export function ReproductorVideo({ punto, automatico }: Props) {
  const { t } = useTranslation();
  const fuente = analizarFuenteVideo(punto.video_url);

  if (!fuente) {
    return (
      <p className="rounded-xl bg-crema-100 p-4 text-sm text-tinta-700">{t('punt.sensVideo')}</p>
    );
  }

  return (
    <div className="space-y-3">
      {fuente.tipo === 'youtube' ? (
        <VideoYoutube id={fuente.id} vertical={fuente.vertical} automatico={automatico} />
      ) : (
        <VideoArchivo url={fuente.url} punto={punto} automatico={automatico} />
      )}

      {punto.video_descripcion ? (
        <details className="rounded-xl border border-crema-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-medium text-tinta-700">
            {t('video.descripcio')}
          </summary>
          <p className="mt-2 text-sm leading-relaxed text-tinta-700">{punto.video_descripcion}</p>
        </details>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function VideoArchivo({
  url,
  punto,
  automatico,
}: {
  url: string;
  punto: PuntoPublico;
  automatico: boolean;
}) {
  const { t } = useTranslation();
  const refVideo = useRef<HTMLVideoElement | null>(null);
  const [bloqueado, setBloqueado] = useState(false);
  const [silenciado, setSilenciado] = useState(false);
  const idioma = idiomaActivo();

  useEffect(() => {
    if (!automatico) return;
    const elemento = refVideo.current;
    if (!elemento) return;

    let cancelado = false;
    void reproducirConRespaldo(elemento).then((resultado) => {
      if (cancelado) return;
      setBloqueado(resultado === 'bloqueado');
      setSilenciado(resultado === 'silenciado');
    });

    return () => {
      cancelado = true;
    };
  }, [automatico, url]);

  return (
    <>
      {/*
        Sin proporcion fija. La serie mezcla piezas verticales grabadas para
        redes con entrevistas horizontales, y un contenedor 16:9 dejaba las
        verticales reducidas a una franja en el centro con dos bandas negras
        enormes a los lados. El video decide su forma y solo se le pone un
        limite de altura para que en un movil no empuje los controles y el
        texto fuera de la pantalla.
      */}
      <div className="flex justify-center overflow-hidden rounded-xl bg-black">
        <video
          ref={refVideo}
          src={url}
          controls
          preload="metadata"
          // playsInline evita que iOS lo abra a pantalla completa por su cuenta,
          // lo que sacaria al usuario del contexto de la ficha del punto.
          playsInline
          className="max-h-[70vh] w-auto max-w-full"
          onPlay={() => {
            setBloqueado(false);
          }}
          // Quien quita el silencio con el control nativo ya no necesita el aviso.
          onVolumeChange={(evento) => {
            if (!evento.currentTarget.muted) setSilenciado(false);
          }}
        >
          {punto.subtitulos_url ? (
            <track
              kind="subtitles"
              src={punto.subtitulos_url}
              srcLang={idioma}
              label={t('video.subtitols')}
              default
            />
          ) : null}
          {t('video.noCompatible')}
        </video>
      </div>

      {bloqueado ? (
        <div className="rounded-xl border border-or-500/40 bg-or-50 p-4">
          <p className="text-sm text-tinta-900">{t('video.bloquejat')}</p>
          <button
            type="button"
            className="boton-primario mt-3 w-full"
            onClick={() => {
              const elemento = refVideo.current;
              if (elemento) void elemento.play().then(() => setBloqueado(false));
            }}
          >
            {t('video.reproduir')}
          </button>
        </div>
      ) : null}

      {silenciado ? (
        <div className="rounded-xl border border-crema-200 bg-crema-100 p-3">
          {/* El mismo aviso que con YouTube: mas vale decirlo que dejar que la
              persona crea que el video no tiene voz. */}
          <p className="text-sm text-tinta-700">{t('video.silenciat')}</p>
          <button
            type="button"
            className="boton-secundario mt-2"
            onClick={() => {
              const elemento = refVideo.current;
              if (!elemento) return;
              // Quitar el silencio desde un toque si esta permitido.
              elemento.muted = false;
              if (elemento.paused) void elemento.play();
              setSilenciado(false);
            }}
          >
            {t('video.activarSo')}
          </button>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */

function VideoYoutube({
  id,
  vertical,
  automatico,
}: {
  id: string;
  vertical: boolean;
  automatico: boolean;
}) {
  const { t } = useTranslation();
  const idioma = idiomaActivo();
  const refMarco = useRef<HTMLIFrameElement | null>(null);
  const [cargado, setCargado] = useState(false);

  const origen = typeof window === 'undefined' ? undefined : window.location.origin;
  const direccion = urlIncrustadaYoutube(id, {
    automatico,
    idioma,
    ...(origen ? { origen } : {}),
  });

  /** Pide al reproductor que haga algo, sin cargar el script de YouTube. */
  const ordenar = useCallback((funcion: 'playVideo' | 'unMute') => {
    refMarco.current?.contentWindow?.postMessage(
      ordenYoutube(funcion),
      'https://www.youtube-nocookie.com',
    );
  }, []);

  /**
   * El arranque automatico por parametro se pierde cuando el iframe se monta
   * mientras la ventana modal todavia se esta abriendo, que es justo lo que
   * ocurre al llegar a un punto. Asi que en cuanto el reproductor esta listo se
   * le pide que arranque, y se insiste un par de veces: el iframe puede tardar
   * en atender mensajes despues de disparar su evento de carga.
   */
  useEffect(() => {
    if (!automatico || !cargado) return;

    const avisos = [0, 400, 1200, 2500].map((retraso) =>
      window.setTimeout(() => {
        ordenar('playVideo');
      }, retraso),
    );

    return () => {
      for (const aviso of avisos) window.clearTimeout(aviso);
    };
  }, [automatico, cargado, ordenar]);

  return (
    <>
      <div
        // El formato corto de YouTube es vertical. Darle proporcion apaisada
        // dejaria dos franjas negras enormes en un movil, que es justo donde se
        // va a ver.
        className={`mx-auto overflow-hidden rounded-xl bg-black ${
          vertical ? 'aspect-[9/16] max-w-xs' : 'aspect-video w-full'
        }`}
      >
        <iframe
          ref={refMarco}
          src={direccion}
          title={t('video.titol')}
          className="h-full w-full"
          // Sin allow no funcionan ni el arranque automatico ni la pantalla
          // completa dentro del iframe.
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          // Sin loading lazy a proposito: dentro de una ventana modal que se
          // acaba de abrir, el navegador puede considerar que el marco todavia
          // no se ve y diferir la carga, con lo que se pierde el arranque.
          //
          // Tampoco lleva sandbox: con el, el reproductor de YouTube no
          // arranca. El aislamiento real lo da que sea otro origen, y los
          // permisos ya estan acotados en allow.
          onLoad={() => {
            setCargado(true);
          }}
        />
      </div>

      {automatico ? (
        <div className="rounded-xl border border-crema-200 bg-crema-100 p-3">
          {/* Con YouTube el arranque automatico solo se permite en silencio.
              Mas vale decirlo que dejar que la persona crea que el video no
              tiene voz. */}
          <p className="text-sm text-tinta-700">{t('video.silenciat')}</p>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="boton-secundario"
              onClick={() => {
                ordenar('playVideo');
              }}
            >
              {t('video.reproduir')}
            </button>
            <button
              type="button"
              className="boton-secundario"
              onClick={() => {
                // El gesto del usuario permite quitar el silencio, cosa que no
                // se puede hacer sola.
                ordenar('unMute');
                ordenar('playVideo');
              }}
            >
              {t('video.activarSo')}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
